import * as React from 'react';
import { useCognitoContext } from '../../providers/cognito.provider';
import type { PasswordResetFormValues } from '@components/organisms/reset-password-form';
import type { ResponseResult } from '@utils';

type Step = 'choose-email' | 'reset-password';

type Props = {
	onPasswordReset: () => void;
};

export const useResetPassword = ({ onPasswordReset }: Props) => {
	const { forgotPassword, resetPassword } = useCognitoContext();

	const [step, setStep] = React.useState<Step>('choose-email');
	const [email, setEmail] = React.useState<string>('');

	const resendCode = React.useCallback(async (): Promise<ResponseResult<any>> => {
		return await forgotPassword(email);
	}, [forgotPassword, email]);

	const sendResetPasswordRequest = React.useCallback(
		async ({ email }: { email: string }): Promise<ResponseResult<any>> => {
			const response = await forgotPassword(email);

			if (response.error) {
				return response;
			}

			setEmail(email);
			setStep('reset-password');

			return response;
		},
		[forgotPassword]
	);

	const handleResetPassword = React.useCallback(
		async ({ code, password }: PasswordResetFormValues) => {
			const response = await resetPassword(email, code, password);

			if (response.error) {
				return response;
			}

			onPasswordReset?.();
			return response;
		},
		[resetPassword, email, onPasswordReset]
	);

	return {
		step,
		email,
		resendCode,
		handleResetPassword,
		sendResetPasswordRequest,
	};
};
